import React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Container, Row } from 'reactstrap';
import StyledDivider from '@whitelabel/component-library/src/styled/StyledDivider';
import { PROTOCOL_DOMAIN } from '@whitelabel/helpers/url';
import Brand from '../Brand';
import rentalcoverLogo from '../../media/images/rentalcover-logo-dark.svg';
import { RENTALCOVER_LOGO_ALT_TEXT, SITE_NAME } from '../../helpers/constants';
import { getButtonProps } from '../../helpers/button';
import messages from './messages';
import {
  StyledFooter,
  StyledLinksWrapper,
  StyledBrandWrapper,
  StyledLinkContainer,
  StyledCollapsibleTextWrapper,
  StyledNoDisclaimerFooter,
  StyledVerticalDivider,
  StyledMinimumCollapsibleTextWrapper,
} from './styledFooter';

interface ILink {
  url: string;
  text: string;
  newWindow?: boolean;
  dataTestId?: string;
}

interface IFooterSection {
  header: React.ReactNode;
  items: ILink[];
}

const termsLink = (intl: IntlShape): ILink => ({
  url: '/terms',
  text: intl.formatMessage(messages.footerLegal3),
  newWindow: true,
  dataTestId: 'footer-terms-link',
});

const privacyLink = (intl: IntlShape): ILink => ({
  url: '/privacy',
  text: intl.formatMessage(messages.footerLegal2),
  newWindow: true,
  dataTestId: 'footer-privacy-link',
});

const footerList = (intl: IntlShape): IFooterSection[] => [
  {
    header: intl.formatMessage(messages.heading1),
    items: [
      {
        url: '/about-us',
        text: intl.formatMessage(messages.footerConnect1),
      },
      {
        url: '/help/about-your-cover/how-to-contact-us',
        text: intl.formatMessage(messages.footerConnect2),
      },
      {
        url: intl.locale === 'en-us' ? `${PROTOCOL_DOMAIN}/blog` : `${PROTOCOL_DOMAIN}/${intl.locale}/blog`,
        text: intl.formatMessage(messages.footerLearn1),
      },
    ],
  },
  {
    header: intl.formatMessage(messages.heading2),
    items: [
      {
        url: '/rental-guides',
        text: intl.formatMessage(messages.footerLearn2),
      },
      {
        url: '/help/about-your-cover/why-use-rentalcover',
        text: intl.formatMessage(messages.footerLearn3),
      },
      {
        url: '/what-is-rental-car-insurance',
        text: intl.formatMessage(messages.footerLearn4),
      },
    ],
  },
  {
    header: intl.formatMessage(messages.heading3),
    items: [
      {
        url: '/accessibility',
        text: intl.formatMessage(messages.footerLegal1),
      },
      {
        url: '/privacy',
        text: intl.formatMessage(messages.footerLegal2),
      },
      {
        url: '/terms',
        text: intl.formatMessage(messages.footerLegal3),
      },
      {
        url: '/modern-slavery-statement',
        text: intl.formatMessage(messages.footerLegal4),
      },
    ],
  },
  {
    header: intl.formatMessage(messages.heading4),
    items: [
      {
        url: '/help',
        text: intl.formatMessage(messages.footerQuick1),
      },
      {
        url: '/claim',
        text: intl.formatMessage(messages.footerQuick2),
      },
      {
        url: '/account',
        text: intl.formatMessage(messages.footerQuick3),
      },
      {
        url: '/llm-info',
        text: intl.formatMessage(messages.footerQuick4),
      },
    ],
  },
];

type LinkPropsGetter = (...props: Parameters<typeof getButtonProps>) => any;

interface IFooter {
  showNavLinks?: boolean;
  showDisclaimer: 'none' | 'extended' | 'collapsed';
  linkPropsGetter?: LinkPropsGetter;
  children?: React.ReactNode;
}

const Footer = ({ showNavLinks, showDisclaimer, linkPropsGetter, children }: IFooter): JSX.Element => {
  const intl = useIntl();

  const brand = {
    title: SITE_NAME,
    logo: rentalcoverLogo,
    alt: RENTALCOVER_LOGO_ALT_TEXT,
  };

  const renderRentalCoverCopyright = () => (
    <span className="footer-copyright" data-test-id="footer-copyright">{`${String.fromCharCode(
      169,
    )} ${SITE_NAME} ${new Date().getFullYear()}`}</span>
  );

  const isRelativeLink = (url: string) => url.startsWith('/');

  // For links, we can only use linkPropsGetter (nextjs link) if it is a relative link
  const linkProps = (item: ILink) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    !!linkPropsGetter && isRelativeLink(item.url)
      ? linkPropsGetter({
          locale: intl.locale,
          link: item.url,
          newWindow: item.newWindow,
        })
      : getButtonProps({
          locale: intl.locale,
          link: item.url,
          newWindow: item.newWindow,
        });

  const renderLink = (item: ILink) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { tag: Tag, ...props } = linkProps(item);

    if (!Tag) return <span>{item.text}</span>;

    return (
      <Tag {...props} data-test-id={item.dataTestId}>
        {item.text}
      </Tag>
    );
  };

  if (showDisclaimer === 'none') {
    return (
      <StyledFooter id="footer">
        <StyledMinimumCollapsibleTextWrapper>
          <StyledNoDisclaimerFooter>
            {renderRentalCoverCopyright()}
            <div className="footer-links" data-test-id="footer-links">
              {renderLink(termsLink(intl))}
              <StyledVerticalDivider />
              {renderLink(privacyLink(intl))}
            </div>
          </StyledNoDisclaimerFooter>
        </StyledMinimumCollapsibleTextWrapper>
      </StyledFooter>
    );
  }

  return (
    <StyledFooter id="footer">
      <StyledLinksWrapper>
        <Container>
          <Row>
            <StyledBrandWrapper>
              <Brand {...brand} />
              {renderRentalCoverCopyright()}
            </StyledBrandWrapper>
          </Row>
        </Container>
        {showNavLinks && (
          <Container>
            <StyledDivider $gap="1.5rem" />
            <Row>
              {footerList(intl).map((column, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledLinkContainer xs="12" md="3" key={index.toString()}>
                  <Row>
                    <b className="footer-column-header">{column.header}</b>
                  </Row>
                  {column.items.map((item) => (
                    <Row key={item.text}>{renderLink(item)}</Row>
                  ))}
                </StyledLinkContainer>
              ))}
            </Row>
          </Container>
        )}
      </StyledLinksWrapper>
      <StyledCollapsibleTextWrapper>{children}</StyledCollapsibleTextWrapper>
    </StyledFooter>
  );
};

export default Footer;
